
import React, { useState, useCallback, useEffect } from 'react';

// Types
import type { UserAnswers, RecommendedWine, Wine, AiRecommendation, AppData, ShopProfile } from './types';
import { AppState } from './types';

// Services
import { getWineRecommendation } from './services/geminiService';
import { syncAllData, getAllSyncedData, setupRealtimeSync, testSync, initializeUserData, saveStaticWineList, addWine } from './services/firestoreService';

// Storage
import { storage } from './utils/storage';

// Firebase
import { auth } from './firebase';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';

// Default Data
import { wineList as defaultWineList, defaultOccasions, defaultTastes, defaultPrices, defaultOrigins } from './data/wineData';

// Components
import Questionnaire from './components/Questionnaire';
import Recommendation from './components/Recommendation';
import LoadingSpinner from './components/LoadingSpinner';
import Settings from './components/Settings';
import SommelierCatIcon from './components/icons/SommelierCatIcon';
import SettingsIcon from './components/icons/SettingsIcon';
import EditWineModal from './components/EditWineModal';
import WineMenu from './components/WineMenu';
import AdminPage from './components/AdminPage';
import CustomerPage from './components/CustomerPage';
import PublicWineList from './components/PublicWineList';
import Auth from './components/Auth';

// Hooks
import { useRouter } from './hooks/useRouter';

const LOCAL_STORAGE_KEY = 'aiSommelierData';

const wineTypeSortOrder: Wine['type'][] = ['スパークリング', 'ロゼ', '白', '赤'];

const sortWineList = (list: Wine[]): Wine[] => {
  return [...list].sort((a, b) => {
    const typeAIndex = wineTypeSortOrder.indexOf(a.type);
    const typeBIndex = wineTypeSortOrder.indexOf(b.type);

    if (typeAIndex !== typeBIndex) {
      return typeAIndex - typeBIndex;
    }
    return a.price - b.price;
  });
};

type ErrorState = { message: string; details: string; } | null;

const getFriendlyAiErrorMessage = (error: unknown): { message: string; details: string } => {
    // Log the full error to the console for easier debugging on any device
    console.error("AI Recommendation Error Details:", error); 

    let friendlyMessage = 'ワインの提案中に予期せぬ問題が発生しました。しばらくしてからもう一度お試しください。';
    let errorDetails = error instanceof Error ? `${error.name}: ${error.message}\n${error.stack}` : String(error);

    if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (message.includes('failed to fetch')) {
            friendlyMessage = 'AIサービスへの接続に失敗しました。ネットワーク接続を確認し、もう一度お試しください。';
        }
        else if (message.includes('api key not valid') || message.includes('api_key') || message.includes('apiキーが設定されていません')) {
            friendlyMessage = 'AIサービスのAPIキーが無効、または設定されていません。';
        }
        else if (message.includes('タイムアウトしました')) {
            friendlyMessage = 'AIへのリクエストがタイムアウトしました。ネットワーク接続を確認するか、しばらくしてから再度お試しください。';
        }
        else if (message.includes('api has not been used') || message.includes('api is not enabled')) {
            friendlyMessage = 'AIサービスが有効になっていません。Google Cloudコンソールで「Generative Language API」を有効にしてください。';
        }
        else if (message.includes('billing account not configured') || message.includes('billing is not enabled')) {
            friendlyMessage = 'プロジェクトで課金が有効になっていません。Google Cloudコンソールで請求先アカウントを有効にしてください。';
        }
        else if (message.includes('permission denied')) {
            friendlyMessage = 'AIサービスへのアクセス権限がありません。IAM設定を確認してください。';
        }
        else if (message.includes('did not return a valid list')) {
            friendlyMessage = 'AIから予期せぬ形式の応答がありました。時間をおいて再度お試しください。もし問題が続く場合は、管理者にご連絡ください。';
        }
        else {
             friendlyMessage = 'AIとの通信中にエラーが発生しました。しばらくしてからもう一度お試しください。';
        }
    }
    
    return { message: friendlyMessage, details: errorDetails };
};


const App: React.FC = () => {
  const { currentRoute, params, navigate } = useRouter();
  const [appState, setAppState] = useState<AppState>(AppState.WELCOME);
  const [answers, setAnswers] = useState<UserAnswers | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendedWine[]>([]);
  const [error, setError] = useState<ErrorState>(null);
  
  // Authentication state
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [lastKnownUser, setLastKnownUser] = useState<User | null>(null);
  const [userSwitchWarning, setUserSwitchWarning] = useState<string | null>(null);
  const [isUserLocked, setIsUserLocked] = useState(false);
  const [lockedUserId, setLockedUserId] = useState<string | null>(null);
  
  // ユーザー別ワインリスト表示用の状態
  const [viewingUserData, setViewingUserData] = useState<AppData | null>(null);
  const [viewingUserLoading, setViewingUserLoading] = useState(false);
  
  // キャッシュ用の状態
  const [userDataCache, setUserDataCache] = useState<Map<string, { data: AppData; timestamp: number }>>(new Map());
  
  // Data states, populated from localStorage or defaults
  const [wineList, setWineList] = useState<Wine[]>([]);
  const [occasions, setOccasions] = useState<string[]>([]);
  const [tastes, setTastes] = useState<string[]>([]);
  const [prices, setPrices] = useState<string[]>([]);
  const [origins, setOrigins] = useState<string[]>([]);
  const [shopProfile, setShopProfile] = useState<ShopProfile>({ name: '', logo: '' });
  
  // ページネーション設定
  const [paginationSettings, setPaginationSettings] = useState({
    enabled: false,
    mode: 'byGenre' as 'byGenre' | 'byCount',
    winesPerPage: 12
  });
  
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [editingWine, setEditingWine] = useState<Wine | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // データ同期の状態
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [shopProfileSyncTimeout, setShopProfileSyncTimeout] = useState<NodeJS.Timeout | null>(null);

  // Load data from localStorage on initial render
  useEffect(() => {
    const loadData = async () => {
      try {
        // 認証済みユーザーの場合はFirestoreからデータを読み込み
        if (user?.uid) {
          console.log('認証済みユーザーです。Firestoreからデータを読み込みます。');
          try {
            console.log('getAllSyncedDataを呼び出します。ユーザーID:', user.uid);
            const result = await getAllSyncedData(user.uid);
            console.log('getAllSyncedDataの結果:', result);
            
            if (result.success && result.data) {
              console.log('Firestoreからデータを読み込みました:', result.data);
              
              // 画像データの詳細をログ出力
              console.log('=== アプリ起動時の画像データ確認 ===');
              result.data.wineList.forEach(wine => {
                console.log(`ワイン${wine.id}(${wine.name})の画像データ:`, wine.image ? `あり（${wine.image.length}文字）` : 'なし');
                if (wine.image) {
                  console.log(`ワイン${wine.id}の画像プレフィックス:`, wine.image.substring(0, 50) + '...');
                }
              });
              console.log('=== 画像データ確認完了 ===');
              
              const data = result.data as AppData;
              setWineList(sortWineList(data.wineList));
              setOccasions(data.occasions);
              setTastes(data.tastes);
              setPrices(data.prices);
              setOrigins(data.origins);
              setShopProfile(data.shopProfile);
              setPaginationSettings(data.paginationSettings || {
                enabled: false,
                mode: 'byGenre',
                winesPerPage: 12
              });
              setLastSyncTime(new Date().toLocaleTimeString());
              setHasUnsavedChanges(false); // 自動同期完了時に変更フラグをリセット
              
              // 自動同期の結果を表示
              const autoSyncMessage = `✅ 自動同期完了！\n` +
                `🍷 ワインリスト: ${result.data.wineList.length}本\n` +
                `🎭 シーン設定: ${result.data.occasions.length}個\n` +
                `👅 味の設定: ${result.data.tastes.length}個\n` +
                `💰 価格設定: ${result.data.prices.length}個\n` +
                `🌍 産地設定: ${result.data.origins.length}個\n` +
                `🏪 店舗名: ${result.data.shopProfile.name || '未設定'}\n` +
                `📥 ログイン時にFirestoreから最新データを取得しました`;
              
              setSyncResult(autoSyncMessage);
              
              // 5秒後に自動同期メッセージを非表示
              setTimeout(() => {
                setSyncResult(null);
              }, 5000);
              
            } else {
              console.log('Firestoreにデータがありません。デフォルトデータを使用します。');
              console.log('getAllSyncedDataのエラー:', result.error);
              
              // エラーの詳細を表示
              const errorMessage = `⚠️ データ取得エラー\n` +
                `エラー: ${result.error || '不明なエラー'}\n` +
                `ユーザーID: ${user.uid}\n` +
                `デフォルトデータを使用します`;
              
              setSyncResult(errorMessage);
              
              // 初期データをFirestoreに保存
              await initializeUserData(user.uid);
              setWineList(sortWineList(defaultWineList));
              setOccasions(defaultOccasions);
              setTastes(defaultTastes);
              setPrices(defaultPrices);
              setOrigins(defaultOrigins);
              setShopProfile({ name: 'AIソムリエ', logo: '' });
              
              // 初期データ作成の結果を表示
              const initMessage = `✅ 初期データを作成しました！\n` +
                `🍷 ワインリスト: ${defaultWineList.length}本\n` +
                `🎭 シーン設定: ${defaultOccasions.length}個\n` +
                `👅 味の設定: ${defaultTastes.length}個\n` +
                `💰 価格設定: ${defaultPrices.length}個\n` +
                `🌍 産地設定: ${defaultOrigins.length}個\n` +
                `🏪 店舗名: AIソムリエ\n` +
                `📤 新規ユーザー用のデフォルトデータをFirestoreに保存しました`;
              
              setSyncResult(initMessage);
              
              // 5秒後に初期データメッセージを非表示
              setTimeout(() => {
                setSyncResult(null);
              }, 5000);
            }
          } catch (error) {
            console.error('Firestoreからのデータ読み込みに失敗:', error);
            // フォールバック: IndexedDB/localStorageから読み込み
      const savedDataResult = await storage.get<AppData>(LOCAL_STORAGE_KEY);
      if (savedDataResult.success && savedDataResult.data) {
        const data = savedDataResult.data;
        setWineList(sortWineList(data.wineList || defaultWineList));
        setOccasions(data.occasions || defaultOccasions);
        setTastes(data.tastes || defaultTastes);
        setPrices(data.prices || defaultPrices);
        setOrigins(data.origins || defaultOrigins);
        setShopProfile(data.shopProfile || { name: 'AIソムリエ', logo: '' });
      } else {
        setWineList(sortWineList(defaultWineList));
        setOccasions(defaultOccasions);
        setTastes(defaultTastes);
        setPrices(defaultPrices);
        setOrigins(defaultOrigins);
        setShopProfile({ name: 'AIソムリエ', logo: '' });
            }
          }
        } else {
          // 未認証の場合はIndexedDB/localStorageから読み込み
          const savedDataResult = await storage.get<AppData>(LOCAL_STORAGE_KEY);
          if (savedDataResult.success && savedDataResult.data) {
            const data = savedDataResult.data;
            setWineList(sortWineList(data.wineList || defaultWineList));
            setOccasions(data.occasions || defaultOccasions);
            setTastes(data.tastes || defaultTastes);
            setPrices(data.prices || defaultPrices);
            setOrigins(data.origins || defaultOrigins);
            setShopProfile(data.shopProfile || { name: 'AIソムリエ', logo: '' });
            setPaginationSettings(data.paginationSettings || {
              enabled: false,
              mode: 'byGenre',
              winesPerPage: 12
            });
          } else {
            setWineList(sortWineList(defaultWineList));
            setOccasions(defaultOccasions);
            setTastes(defaultTastes);
            setPrices(defaultPrices);
            setOrigins(defaultOrigins);
            setShopProfile({ name: 'AIソムリエ', logo: '' });
            setPaginationSettings({
              enabled: false,
              mode: 'byGenre',
              winesPerPage: 12
            });
          }
      }
    } catch (e) {
        console.error("Failed to load data", e);
      // Fallback to defaults
      setWineList(sortWineList(defaultWineList));
      setOccasions(defaultOccasions);
      setTastes(defaultTastes);
      setPrices(defaultPrices);
      setOrigins(defaultOrigins);
      setShopProfile({ name: 'AIソムリエ', logo: '' });
      setPaginationSettings({
        enabled: false,
        mode: 'byGenre',
        winesPerPage: 12
      });
    } finally {
        setIsDataLoaded(true);
    }
    };

    loadData();
  }, [user?.uid]); // ユーザーIDが変更された時に再実行

  // Monitor authentication state
  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('=== 認証状態変更 ===');
      console.log('新しいユーザー:', user ? {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified,
        lastSignInTime: user.metadata?.lastSignInTime,
        creationTime: user.metadata?.creationTime
      } : 'null');
      console.log('現在のルート:', currentRoute);
      console.log('認証ローディング状態:', authLoading);
      console.log('=== 認証状態変更完了 ===');
      
      // ユーザーロック機能：一度ログインしたユーザーは明示的にログアウトするまで変更不可
      if (isUserLocked && lockedUserId && user && user.uid !== lockedUserId) {
        console.warn('🚫 ロックされたユーザー以外への切り替えを検出！無視します。');
        console.warn('ロックされたユーザー:', lockedUserId);
        console.warn('切り替えようとしたユーザー:', user.email, user.uid);
        
        // ロックされたユーザーに戻す
        if (lastKnownUser && lastKnownUser.uid === lockedUserId) {
          console.log('ロックされたユーザーに戻します');
          setUser(lastKnownUser);
        } else {
          console.log('ロックされたユーザーが見つからないため、ログアウトします');
          setUser(null);
        }
        setAuthLoading(false);
        return;
      }

      // 初回ログイン時またはロックされていない場合のみユーザーを更新
      if (!isUserLocked && user) {
        console.log('🔒 ユーザーをロックします:', user.email, user.uid);
        setIsUserLocked(true);
        setLockedUserId(user.uid);
      }

      // ログアウト時はロックを解除
      if (!user) {
        console.log('🔓 ユーザーロックを解除します');
        setIsUserLocked(false);
        setLockedUserId(null);
        setUserSwitchWarning(null);
      }

      // ユーザー切り替えの検出（ロックされていない場合のみ）
      if (lastKnownUser && user && lastKnownUser.uid !== user.uid && !isUserLocked) {
        console.warn('⚠️ ユーザーが切り替わりました！');
        console.warn('前のユーザー:', lastKnownUser.email, lastKnownUser.uid);
        console.warn('新しいユーザー:', user.email, user.uid);
        
        setUserSwitchWarning(
          `⚠️ ユーザーが自動的に切り替わりました！\n\n` +
          `前のユーザー: ${lastKnownUser.email}\n` +
          `新しいユーザー: ${user.email}\n\n` +
          `このまま続行する場合は「続行」を、` +
          `前のユーザーに戻る場合は「ログアウト」をクリックしてください。`
        );
      } else if (lastKnownUser && !user) {
        console.log('ユーザーがログアウトしました');
        setUserSwitchWarning(null);
      } else if (!lastKnownUser && user) {
        console.log('ユーザーがログインしました');
        setUserSwitchWarning(null);
      }
      
      setLastKnownUser(user);
      setUser(user);
      setAuthLoading(false);
      
      // 自動リダイレクトを無効化（renderContentで適切に処理する）
      // if (!user && currentRoute === '/admin') {
      //   console.log('Redirecting unauthenticated user from /admin to /'); // デバッグ用
      //   navigate('/');
      // }
    });

    return unsubscribe;
  }, [auth, currentRoute, navigate]);

  // ユーザー別ワインリストの読み込み
  useEffect(() => {
    const loadUserData = async () => {
      console.log('=== ユーザー別データ読み込み開始 ===');
      console.log('currentRoute:', currentRoute);
      console.log('params.userId:', params.userId);
      console.log('params:', params);
      
      if (currentRoute === '/wine-list' && params.userId) {
        setViewingUserLoading(true);
        try {
          console.log('ユーザー別ワインリスト読み込み開始:', params.userId);
          console.log('getAllSyncedData関数:', typeof getAllSyncedData);
          
          // キャッシュチェック（5分間有効）
          const cacheKey = params.userId;
          const cached = userDataCache.get(cacheKey);
          const now = Date.now();
          const cacheExpiry = 5 * 60 * 1000; // 5分
          
          if (cached && (now - cached.timestamp) < cacheExpiry) {
            console.log('キャッシュからデータを読み込み:', cached.data);
            setViewingUserData(cached.data);
            setViewingUserLoading(false);
            return;
          }
          
          console.log('Firestoreからデータを読み込み');
          const result = await getAllSyncedData(params.userId);
          console.log('getAllSyncedData結果:', result);
          console.log('result.success:', result.success);
          console.log('result.data:', result.data);
          console.log('result.error:', result.error);
          
          if (result.success && result.data) {
            setViewingUserData(result.data);
            console.log('ユーザー別ワインリスト読み込み完了:', result.data);
            console.log('ワイン数:', result.data.wineList?.length || 0);
            
            // キャッシュに保存
            setUserDataCache(prev => {
              const newCache = new Map(prev);
              newCache.set(cacheKey, { data: result.data, timestamp: now });
              console.log('データをキャッシュに保存:', cacheKey);
              return newCache;
            });
          } else {
            console.error('ユーザー別ワインリスト読み込み失敗:', result.error);
            
            // テスト用：固定データを使用
            console.log('テスト用固定データを使用します');
            const testData = {
              wineList: [
                {
                  id: 1,
                  name: 'テストワイン1',
                  price: '¥3,000',
                  origin: 'フランス',
                  taste: 'フルボディ',
                  occasion: 'ディナー',
                  visible: true,
                  image: ''
                },
                {
                  id: 2,
                  name: 'テストワイン2',
                  price: '¥2,500',
                  origin: 'イタリア',
                  taste: 'ライト',
                  occasion: 'ランチ',
                  visible: true,
                  image: ''
                }
              ],
              occasions: ['ディナー', 'ランチ'],
              tastes: ['フルボディ', 'ライト'],
              prices: ['¥3,000', '¥2,500'],
              origins: ['フランス', 'イタリア'],
              shopProfile: { name: 'テスト店舗', logo: '' }
            };
            
            console.log('テスト用データを設定:', testData);
            setViewingUserData(testData);
          }
        } catch (error) {
          console.error('ユーザー別ワインリスト読み込みエラー:', error);
          console.error('エラーの詳細:', error);
          setViewingUserData(null);
        } finally {
          setViewingUserLoading(false);
        }
      } else {
        console.log('条件に合わないため、viewingUserDataをnullに設定');
        console.log('currentRoute === "/wine-list":', currentRoute === '/wine-list');
        console.log('params.userId存在:', !!params.userId);
        setViewingUserData(null);
      }
    };

    loadUserData();
  }, [currentRoute, params.userId]);

  // Save data to IndexedDB/localStorage whenever it changes
  useEffect(() => {
    if (!isDataLoaded) return; // Prevent saving before initial load
    
    const saveData = async () => {
      try {
        const data: AppData = {
          wineList,
          occasions,
          tastes,
          prices,
          origins,
          shopProfile,
        };
        
        // IndexedDB優先、localStorageフォールバックで保存
        const result = await storage.set(LOCAL_STORAGE_KEY, data);
        if (result.success) {
          console.log('✅ [App] データをストレージに保存しました');
          
          // データが変更されたことを検知
          setHasUnsavedChanges(true);
          
          // データ同期も実行（Firestoreエラー解決まで一時的に無効化）
          /*
          if (!lastSyncTime) {
            handleDataSync(data);
          }
          */
        } else {
          console.error('❌ [App] データの保存に失敗:', result.error);
        }
      } catch (e) {
        console.error("Failed to save data to storage", e);
      }
    };
    
    saveData();
  }, [isDataLoaded, wineList, occasions, tastes, prices, origins, shopProfile]);

  // データ同期の処理
  const handleDataSync = async (data: AppData): Promise<any> => {
    if (isSyncing) {
      console.log('既に同期中のため、新しい同期を開始しません');
      throw new Error('既に同期中です');
    }
    
    try {
      console.log('=== データ同期開始 ===');
      console.log('同期するワイン数:', data.wineList.length);
      console.log('同期するワインID一覧:', data.wineList.map(w => w.id));
      setIsSyncing(true);
      setSyncError(null);
      
      // 認証されたユーザーIDを取得
      const userId = user?.uid || 'default-user';
      console.log('同期先ユーザーID:', userId);
      
      // タイムアウト付きで同期実行（30秒）
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('同期がタイムアウトしました')), 30000);
      });
      
      const syncPromise = syncAllData(userId, data);
      const result = await Promise.race([syncPromise, timeoutPromise]);
      
      console.log('=== 同期結果の詳細 ===');
      console.log('同期結果:', result);
      console.log('同期成功:', result.success);
      console.log('同期エラー:', result.error);
      
      // 同期成功時に静的HTMLを生成・保存
      if (result.success) {
        console.log('=== 静的HTML生成開始 ===');
        console.log('同期されたデータ:', data);
        console.log('ワイン数:', data.wineList.length);
        console.log('ワイン一覧:', data.wineList.map(w => ({ id: w.id, name: w.name, visible: w.visible })));
        
        try {
          const staticHtmlResult = await saveStaticWineList(userId, data);
          console.log('静的HTML生成結果:', staticHtmlResult);
          
          if (staticHtmlResult.success) {
            console.log('✅ === 静的HTML生成成功 ===');
            console.log('✅ 静的HTML URL:', staticHtmlResult.data?.url);
            console.log('✅ 静的HTML生成時刻:', new Date(staticHtmlResult.data?.timestamp || 0).toLocaleString('ja-JP'));
            console.log('✅ 静的HTMLサイズ:', staticHtmlResult.data?.html.length || 0, '文字');
            console.log('✅ 静的URL更新: 成功');
            
            // 静的HTMLの内容を一部表示
            if (staticHtmlResult.data?.html) {
              const htmlPreview = staticHtmlResult.data.html.substring(0, 500);
              console.log('静的HTMLプレビュー:', htmlPreview + '...');
            }
          } else {
            console.error('❌ === 静的HTML生成失敗 ===');
            console.error('❌ 静的HTML生成失敗:', staticHtmlResult.error);
            console.error('❌ 静的HTML生成失敗の詳細:', staticHtmlResult);
            console.error('❌ 静的URL更新: 失敗');
          }
        } catch (staticError) {
          console.error('❌ === 静的HTML生成エラー ===');
          console.error('❌ 静的HTML生成エラー:', staticError);
          console.error('❌ 静的HTML生成エラーの詳細:', staticError);
          console.error('❌ 静的URL更新: エラー');
        }
      } else {
        console.log('⚠️ データ同期が失敗したため、静的HTML生成をスキップします');
        console.log('⚠️ 静的URL更新: スキップ（同期失敗）');
      }
      
      if (result.success) {
        setLastSyncTime(new Date().toLocaleTimeString());
        console.log('データが正常に同期されました');
        console.log('=== データ同期完了 ===');
        
        // 静的URL更新情報をログに追加
        console.log('=== 静的URL更新情報 ===');
        if (user?.uid) {
          const staticUrl = `http://wine.orange-ark.jp/static-wine-list/${user.uid}.html`;
          console.log('📱 静的HTML URL:', staticUrl);
          console.log('📱 QRコード用URL:', staticUrl);
          console.log('📱 顧客はこのURLでワインリストにアクセスできます');
          console.log('📱 静的URL更新: 完了');
        } else {
          console.log('⚠️ ユーザーIDが取得できません');
          console.log('⚠️ 静的URL更新: 失敗（ユーザーIDなし）');
        }
        console.log('=== 静的URL更新情報完了 ===');
        return result; // 成功結果を返す
      } else {
        const errorMessage = result.error || 'データの同期に失敗しました';
        setSyncError(errorMessage);
        console.error('データ同期エラー:', result.error);
        console.error('同期失敗の詳細:', result);
        console.log('=== データ同期失敗 ===');
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'データの同期中にエラーが発生しました';
      setSyncError(errorMessage);
      console.error('データ同期エラー:', error);
      console.log('=== データ同期エラー ===');
      throw error; // エラーを再スロー
    } finally {
      console.log('データ同期処理を終了します');
      setIsSyncing(false);
    }
  };

  // リアルタイム同期の設定（完全に無効化）
  // リアルタイム同期は無効化されています
  // 手動同期のみを使用してください

  // デバッグ用：同期状態の監視
  useEffect(() => {
    console.log('同期状態が変更されました:', { isSyncing, lastSyncTime, syncError });
  }, [isSyncing, lastSyncTime, syncError]);

  // コンポーネントのクリーンアップ時にタイムアウトをクリア
  useEffect(() => {
    return () => {
      if (shopProfileSyncTimeout) {
        clearTimeout(shopProfileSyncTimeout);
      }
    };
  }, [shopProfileSyncTimeout]);

  const navigateToWelcome = () => {
    setAppState(AppState.WELCOME);
    setAnswers(null);
    setRecommendations([]);
    setError(null);
  };

  const handleGoToSettings = () => {
    navigate('/admin');
  };

  const handleAdminAccess = () => {
    console.log('handleAdminAccess called, navigating to /admin'); // デバッグ用
    navigate('/admin');
  };

  const handleLogout = async () => {
    try {
      console.log('🔓 ログアウト処理開始');
      
      // ユーザーロックを解除
      setIsUserLocked(false);
      setLockedUserId(null);
      setUserSwitchWarning(null);
      
      if (auth) {
        await signOut(auth);
        console.log('🔓 Firebaseからログアウト完了');
      }
      
      // ログアウト後は認証画面に遷移
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // デバッグ用：認証状態を強制リセット
  const handleForceLogout = () => {
    console.log('🔓 強制ログアウト処理開始');
    
    // ユーザーロックを解除
    setIsUserLocked(false);
    setLockedUserId(null);
    setUserSwitchWarning(null);
    
    setUser(null);
    setAuthLoading(false);
    navigate('/auth');
  };
  
  const handleStart = () => setAppState(AppState.QUESTIONNAIRE);
  const handleShowWineMenu = () => setAppState(AppState.WINE_MENU);
  
  const parseCsvToWine = (csvText: string): Wine[] => {
    try {
      const lines = csvText.trim().split('\n');
      const headerLine = lines.shift();
      if (!headerLine) throw new Error("CSV is empty or has no header.");

      const headers = headerLine.trim().split(',').map(h => h.trim());
      const requiredHeaders: (keyof Omit<Wine, 'priceRange' | 'image' | 'visible'> | 'price')[] = ['id', 'name', 'type', 'origin', 'variety', 'price', 'taste', 'characteristics'];
      
      for (const requiredHeader of requiredHeaders) {
          if (!headers.includes(requiredHeader)) {
              throw new Error(`Missing required CSV header: ${requiredHeader}`);
          }
      }

      return lines.map((line, index) => {
          if (line.trim() === '') return null;
          const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(v => v.replace(/^"|"$/g, '').trim()) || [];
          
          if (values.length !== headers.length) {
              console.warn(`Row ${index + 2}: Column count mismatch. Expected ${headers.length}, found ${values.length}. Line: "${line}"`);
              return null;
          }

          const wineData = headers.reduce((obj, header, i) => {
              const key = header.trim();
              const value = values[i] || '';
              if (key === 'id' || key === 'price') {
                  obj[key] = parseInt(value, 10);
              } else if (key === 'characteristics') {
                  obj[key] = value.split(';').map(s => s.trim());
              } else {
                   obj[key] = value;
              }
              return obj;
          }, {} as any);
          
          wineData.image = '';
          wineData.visible = true;

          if (isNaN(wineData.id) || !wineData.name || isNaN(wineData.price)) {
               console.warn(`Row ${index + 2}: Invalid or missing data for id, name, or price. Skipping.`);
               return null;
          }
          
          return wineData as Wine;
      }).filter((wine): wine is Wine => wine !== null);
    } catch (e) {
      console.error("CSV parsing error:", e);
      throw new Error("CSVファイルの形式が正しくありません。ヘッダー（id,name,type,origin,variety,price,taste,characteristics）を確認し、データがカンマ区切りになっているか確認してください。");
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvError(null);
    setCsvFileName(null);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        try {
          const parsedWines = parseCsvToWine(text);
          const sortedWines = sortWineList(parsedWines);
          setWineList(sortedWines);
          setCsvFileName(file.name);
          
                // CSVインポート後に明示的に同期を実行（Firestoreエラー解決まで一時的に無効化）
          /*
          const data: AppData = {
            wineList: sortedWines,
            occasions,
            tastes,
            prices,
            origins,
            shopProfile,
          };
          handleDataSync(data);
          */
        } catch (err: any) {
          setCsvError(err.message);
        }
      }
    };
    reader.onerror = () => {
      setCsvError("ファイルの読み込みに失敗しました。");
    };
    reader.readAsText(file);
  };

  const handleAddWine = async (newWine: Omit<Wine, 'id' | 'image'> & { image?: string }) => {
    console.log('=== ワイン追加開始 ===');
    console.log('追加するワイン名:', newWine.name);
    console.log('追加するワインの画像データ:', newWine.image ? `あり（${newWine.image.length}文字）` : 'なし');
    if (newWine.image) {
      console.log('追加するワインの画像プレフィックス:', newWine.image.substring(0, 50) + '...');
    }
    console.log('追加前のワイン数:', wineList.length);
    console.log('追加前のワインID一覧:', wineList.map(w => w.id));
    
    // 一時的なIDを生成（楽観的UI用）
    const tempId = wineList.length > 0 ? Math.max(...wineList.map(w => w.id), 0) + 1 : 1;
    const tempWine: Wine = { ...newWine, id: tempId, image: newWine.image || '' };
    const tempUpdatedList = sortWineList([...wineList, tempWine]);
    
    // 楽観的UI更新
    setWineList(tempUpdatedList);
    
    console.log('楽観的UI更新完了 - 一時ID:', tempId);
    
    try {
      // Firestoreに直接追加（IDはFirestoreサービスで生成）
      console.log('=== Firestore直接追加開始 ===');
      console.log('ユーザーID:', user?.uid);
      
      const addResult = await addWine(user?.uid || '', tempWine);
      
      if (addResult.success) {
        console.log('=== ワイン追加成功 ===');
        console.log('Firestoreに保存されました');
        
        // 成功時は楽観的UIをそのまま維持
        console.log('楽観的UIを維持します');
      } else {
        console.error('=== ワイン追加失敗 ===');
        console.error('エラー:', addResult.error);
        
        // 失敗時は楽観的UIをロールバック
        setWineList(wineList);
        console.log('楽観的UIをロールバックしました');
        
        alert(`❌ ワイン追加エラー！\nワイン名: ${newWine.name}\nエラー: ${addResult.error}\nローカルに追加されましたが、Firestoreに保存できませんでした`);
        return;
      }
      
    } catch (error) {
      console.error('=== ワイン追加エラー ===', error);
      console.error('エラーの詳細:', error);
      console.error('エラーの型:', typeof error);
      console.error('エラーのメッセージ:', error instanceof Error ? error.message : '不明');
      console.error('エラーのスタック:', error instanceof Error ? error.stack : '不明');
      
      // 失敗時は楽観的UIをロールバック
      setWineList(wineList);
      console.log('楽観的UIをロールバックしました');
      
      // エラーメッセージを表示
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      alert(`❌ ワイン追加エラー！\nワイン名: ${newWine.name}\nエラー: ${errorMessage}\nローカルでは追加されましたが、Firestoreに保存できませんでした\n\n詳細はコンソールを確認してください`);
    }
  };

  const handleRemoveWine = async (wineIdToRemove: number) => {
    // 即座にアラートを表示して処理開始を確認
    alert(`🔍 ワイン削除処理開始\nワインID: ${wineIdToRemove}\n削除前: ${wineList.length}本`);
    
    console.log('=== ワイン削除開始 ===');
    console.log('削除対象ワインID:', wineIdToRemove);
    console.log('削除前のワイン数:', wineList.length);
    console.log('削除前のワインID一覧:', wineList.map(w => w.id));
    
    const updatedList = sortWineList(wineList.filter(w => w.id !== wineIdToRemove));
    setWineList(updatedList);
    
    console.log('削除後のワイン数:', updatedList.length);
    console.log('削除後のワインID一覧:', updatedList.map(w => w.id));
    
    // 削除後のデータをFirestoreに同期
    const data: AppData = {
      wineList: updatedList,
      occasions,
      tastes,
      prices,
      origins,
      shopProfile,
    };
    
    console.log('Firestoreに同期するデータ:', data);
    
    try {
      // 同期処理を待機
      console.log('=== Firestore同期開始 ===');
      console.log('同期するデータ:', data);
      console.log('ユーザーID:', user?.uid);
      
      const syncResult = await handleDataSync(data);
      console.log('=== ワイン削除完了（Firestore同期成功） ===');
      console.log('同期結果:', syncResult);
      
      // 成功メッセージを表示
      alert(`✅ ワイン削除完了！\nワインID: ${wineIdToRemove}\n削除後: ${updatedList.length}本\nFirestoreに保存されました\n\n同期結果: ${JSON.stringify(syncResult)}`);
      
      // DBの状態を確認
      try {
        console.log('=== DB状態確認開始 ===');
        console.log('確認するユーザーID:', user?.uid);
        console.log('削除したワインID:', wineIdToRemove);
        
        const dbCheckResult = await getAllSyncedData(user?.uid || '');
        console.log('=== DB状態確認の詳細結果 ===');
        console.log('dbCheckResult:', dbCheckResult);
        console.log('dbCheckResult.success:', dbCheckResult.success);
        console.log('dbCheckResult.data:', dbCheckResult.data);
        console.log('dbCheckResult.error:', dbCheckResult.error);
        
        if (dbCheckResult.success && dbCheckResult.data) {
          const dbWineCount = dbCheckResult.data.wineList.length;
          const dbWineIds = dbCheckResult.data.wineList.map(w => w.id);
          const deletedWineExists = dbWineIds.includes(wineIdToRemove);
          
          console.log('=== DB状態確認結果 ===');
          console.log('DBのワイン数:', dbWineCount);
          console.log('DBのワインID一覧:', dbWineIds);
          console.log('削除したワインIDがDBに存在するか:', deletedWineExists);
          console.log('ローカルのワイン数:', updatedList.length);
          console.log('ローカルのワインID一覧:', updatedList.map(w => w.id));
          
          // DB状態の結果をアラートで表示
          alert(`🔍 DB状態確認結果\n` +
            `DBのワイン数: ${dbWineCount}本\n` +
            `ローカルのワイン数: ${updatedList.length}本\n` +
            `削除したワインID: ${wineIdToRemove}\n` +
            `削除したワインがDBに存在するか: ${deletedWineExists ? 'はい（削除失敗）' : 'いいえ（削除成功）'}\n` +
            `DBのワインID一覧: ${dbWineIds.join(', ')}\n` +
            `ローカルのワインID一覧: ${updatedList.map(w => w.id).join(', ')}`);
        } else {
          console.error('DB状態確認失敗:', dbCheckResult.error);
          alert(`❌ DB状態確認失敗\nエラー: ${dbCheckResult.error}\n詳細: ${JSON.stringify(dbCheckResult, null, 2)}`);
        }
      } catch (dbError) {
        console.error('DB状態確認エラー:', dbError);
        alert(`❌ DB状態確認エラー\nエラー: ${dbError instanceof Error ? dbError.message : '不明なエラー'}`);
      }
      
    } catch (error) {
      console.error('=== ワイン削除エラー ===', error);
      console.error('エラーの詳細:', error);
      console.error('エラーの型:', typeof error);
      console.error('エラーのメッセージ:', error instanceof Error ? error.message : '不明');
      console.error('エラーのスタック:', error instanceof Error ? error.stack : '不明');
      
      // エラーメッセージを表示
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      alert(`❌ ワイン削除エラー！\nワインID: ${wineIdToRemove}\nエラー: ${errorMessage}\nローカルでは削除されましたが、Firestoreに保存できませんでした\n\n詳細はコンソールを確認してください\n\nエラーの詳細:\n${JSON.stringify(error, null, 2)}`);
    }
  };
  
  const handleToggleWineVisibility = (wineIdToToggle: number) => {
    const updatedList = sortWineList(
      wineList.map(wine => 
          wine.id === wineIdToToggle ? { ...wine, visible: !wine.visible } : wine
      )
    );
    setWineList(updatedList);
    
    // 可視性変更後のデータをFirestoreに同期
    const data: AppData = {
      wineList: updatedList,
      occasions,
      tastes,
      prices,
      origins,
      shopProfile,
    };
    handleDataSync(data);
  };

  const handleStartEdit = (wineToEdit: Wine) => setEditingWine(wineToEdit);
  const handleCancelEdit = () => setEditingWine(null);

  const handleUpdateWine = (updatedWine: Wine) => {
    const updatedList = sortWineList(wineList.map(w => w.id === updatedWine.id ? updatedWine : w));
    setWineList(updatedList);
    setEditingWine(null);
    
    // 編集後のデータをFirestoreに同期
    const data: AppData = {
      wineList: updatedList,
      occasions,
      tastes,
      prices,
      origins,
      shopProfile,
    };
    handleDataSync(data);
  };

  const handleAddOption = (type: 'occasions' | 'tastes' | 'prices' | 'origins', newOption: string) => {
    const trimmedOption = newOption.trim();
    if (!trimmedOption) return;

    let updatedOccasions = occasions;
    let updatedTastes = tastes;
    let updatedPrices = prices;
    let updatedOrigins = origins;

    switch (type) {
      case 'occasions':
        if (!occasions.includes(trimmedOption)) {
          updatedOccasions = [...occasions, trimmedOption];
          setOccasions(updatedOccasions);
        }
        break;
      case 'tastes':
        if (!tastes.includes(trimmedOption)) {
          updatedTastes = [...tastes, trimmedOption];
          setTastes(updatedTastes);
        }
        break;
      case 'prices':
        if (!prices.includes(trimmedOption)) {
          updatedPrices = [...prices, trimmedOption];
          setPrices(updatedPrices);
        }
        break;
      case 'origins':
        if (!origins.includes(trimmedOption)) {
          updatedOrigins = [...origins, trimmedOption];
          setOrigins(updatedOrigins);
        }
        break;
    }

    // 設定変更後に明示的に同期を実行
    const data: AppData = {
      wineList,
      occasions: updatedOccasions,
      tastes: updatedTastes,
      prices: updatedPrices,
      origins: updatedOrigins,
      shopProfile,
    };
    handleDataSync(data);
  };

  const handleRemoveOption = (type: 'occasions' | 'tastes' | 'prices' | 'origins', optionToRemove: string) => {
    if (optionToRemove === '指定なし') return;
    
    let updatedOccasions = occasions;
    let updatedTastes = tastes;
    let updatedPrices = prices;
    let updatedOrigins = origins;

    switch (type) {
      case 'occasions':
        updatedOccasions = occasions.filter(o => o !== optionToRemove);
        setOccasions(updatedOccasions);
        break;
      case 'tastes':
        updatedTastes = tastes.filter(o => o !== optionToRemove);
        setTastes(updatedTastes);
        break;
      case 'prices':
        updatedPrices = prices.filter(o => o !== optionToRemove);
        setPrices(updatedPrices);
        break;
      case 'origins':
        updatedOrigins = origins.filter(o => o !== optionToRemove);
        setOrigins(updatedOrigins);
        break;
    }

    // 設定変更後に明示的に同期を実行
    const data: AppData = {
      wineList,
      occasions: updatedOccasions,
      tastes: updatedTastes,
      prices: updatedPrices,
      origins: updatedOrigins,
      shopProfile,
    };
    handleDataSync(data);
  };

  const handleUpdateShopProfile = async (profileUpdate: Partial<ShopProfile>) => {
    const updatedProfile = { ...shopProfile, ...profileUpdate };
    setShopProfile(updatedProfile);
    
    // 既存のタイムアウトをクリア
    if (shopProfileSyncTimeout) {
      clearTimeout(shopProfileSyncTimeout);
    }
    
    // デバウンス機能：500ms後に同期を実行
    const timeout = setTimeout(async () => {
      if (!isSyncing) {
        const data: AppData = {
          wineList,
          occasions,
          tastes,
          prices,
          origins,
          shopProfile: updatedProfile,
        };
        try {
          await handleDataSync(data);
        } catch (error) {
          console.log('店舗情報の同期をスキップしました:', error);
        }
      } else {
        console.log('同期中のため、店舗情報の同期をスキップします');
      }
    }, 500);
    
    setShopProfileSyncTimeout(timeout);
  };
  
  const handleImportData = async (data: AppData): Promise<boolean> => {
    try {
        if (
            !data.shopProfile ||
            !Array.isArray(data.wineList) ||
            !Array.isArray(data.occasions) ||
            !Array.isArray(data.tastes) ||
            !Array.isArray(data.prices) ||
            !Array.isArray(data.origins)
        ) {
            throw new Error("Invalid data structure.");
        }

        setWineList(sortWineList(data.wineList));
        setOccasions(data.occasions);
        setTastes(data.tastes);
        setPrices(data.prices);
        setOrigins(data.origins);
        setShopProfile(data.shopProfile);
        
        return true;
    } catch (error) {
        console.error("Failed to import data:", error);
        return false;
    }
  };

  const handleSubmit = useCallback(async (userAnswers: UserAnswers) => {
    setAnswers(userAnswers);
    setAppState(AppState.LOADING);
    setError(null);
    setRecommendations([]);

    try {
      const result: AiRecommendation[] = await getWineRecommendation(userAnswers, wineList);
      if (result && result.length > 0) {
        const recommendationsWithImages = result.map(rec => {
            const matchedWine = wineList.find(wine => Number(wine.id) === Number(rec.id));
            return {
                ...rec,
                image: matchedWine?.image || ''
            };
        });
        setRecommendations(recommendationsWithImages);
        setAppState(AppState.RESULT);
      } else {
        setError({
            message: 'AIからの応答がありませんでした。もう一度お試しください。',
            details: 'The AI service returned an empty or invalid response.'
        });
        setAppState(AppState.QUESTIONNAIRE);
      }
    } catch (e) {
      const errorInfo = getFriendlyAiErrorMessage(e);
      setError(errorInfo);
      setAppState(AppState.QUESTIONNAIRE);
    }
  }, [wineList]);
  
  if (!isDataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <LoadingSpinner type="loading" title="読み込み中" />
      </div>
    );
  }

  // ユーザー切り替え警告の表示
  if (userSwitchWarning) {
    return (
      <div className="bg-[#FDFBF7] min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="text-yellow-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>ユーザーが切り替わりました</h2>
            <div className="mb-6 whitespace-pre-line text-sm" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
              {userSwitchWarning}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setUserSwitchWarning(null)}
                className="flex-1 bg-red-800 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-900 transition-colors"
              >
                続行
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    console.log('renderContent called:', { currentRoute, user, authLoading, auth: !!auth }); // デバッグ用
    
    // 認証中はローディング表示
    if (authLoading) {
      console.log('Showing loading spinner'); // デバッグ用
      return <LoadingSpinner type="loading" title="読み込み中" />;
    }

    // 認証画面
    if (currentRoute === '/auth') {
      return <Auth onLoginSuccess={() => navigate('/admin')} />;
    }

    // ユーザー別ワインリストページ
    if (currentRoute === '/wine-list') {
      if (viewingUserLoading) {
        return (
          <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
            <LoadingSpinner type="loading" title="読み込み中" />
          </div>
        );
      }

      if (!viewingUserData) {
        return (
          <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>ワインリストが見つかりません</h1>
              <p className="mb-6" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>指定されたユーザーのワインリストが存在しないか、アクセスできません。</p>
              
              {/* デバッグ情報を表示 */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">デバッグ情報:</h3>
                <p className="text-xs text-yellow-700 mb-1">URL: {window.location.href}</p>
                <p className="text-xs text-yellow-700 mb-1">userId: {params.userId || '未設定'}</p>
                <p className="text-xs text-yellow-700 mb-1">currentRoute: {currentRoute}</p>
                <p className="text-xs text-yellow-700 mb-1">viewingUserLoading: {String(viewingUserLoading)}</p>
                <p className="text-xs text-yellow-700">キャッシュサイズ: {userDataCache.size}件</p>
                <p className="text-xs text-yellow-700">キャッシュ有効: {userDataCache.has(params.userId || '') ? 'あり' : 'なし'}</p>
              </div>
              
              <button
                onClick={() => navigate('/')}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg transition-colors"
              >
                ホームに戻る
              </button>
            </div>
          </div>
        );
      }

      return (
        <WineMenu
          wineList={viewingUserData.wineList}
          onBack={() => navigate('/')}
          userId={params.userId}
        />
      );
    }

    // 動的ワインリストページ（ログイン不要）
    if (currentRoute === '/public-wine-list') {
      if (!params.userId) {
        return (
          <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>ユーザーIDが指定されていません</h1>
              <p className="mb-6" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>URLにユーザーIDが含まれていません。</p>
              <button
                onClick={() => navigate('/')}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg transition-colors"
              >
                ホームに戻る
              </button>
            </div>
          </div>
        );
      }

      return <PublicWineList userId={params.userId} onBack={navigateToWelcome} />;
    }

    // 管理者ページへのアクセス制御
    if (currentRoute === '/admin') {
      console.log('Admin route detected, user:', user); // デバッグ用
      
      // 認証チェック
      if (!user) {
        console.log('Admin access denied: no user');
        return <Auth onLoginSuccess={() => navigate('/admin')} />;
      }
      
      console.log('=== 管理者画面レンダリング ===');
      console.log('ユーザー:', user ? user.email : '未認証');
      console.log('ワインリスト数:', wineList.length);
      console.log('handleRemoveWine関数:', typeof handleRemoveWine);
      return (
        <AdminPage
          onBack={() => navigate('/')}
                  onFileChange={handleFileChange}
                  onAddWine={handleAddWine}
                  onRemoveWine={handleRemoveWine}
                  onStartEdit={handleStartEdit}
                  onToggleWineVisibility={handleToggleWineVisibility}
                  csvFileName={csvFileName}
                  csvError={csvError}
                  wineList={wineList}
                  occasions={occasions}
                  onAddOccasion={(occasion) => handleAddOption('occasions', occasion)}
                  onRemoveOccasion={(occasion) => handleRemoveOption('occasions', occasion)}
                  tastes={tastes}
                  onAddTaste={(taste) => handleAddOption('tastes', taste)}
                  onRemoveTaste={(taste) => handleRemoveOption('tastes', taste)}
                  prices={prices}
                  onAddPrice={(price) => handleAddOption('prices', price)}
                  onRemovePrice={(price) => handleRemoveOption('prices', price)}
                  origins={origins}
                  onAddOrigin={(origin) => handleAddOption('origins', origin)}
                  onRemoveOrigin={(origin) => handleRemoveOption('origins', origin)}
                  onImportData={handleImportData}
                  shopProfile={shopProfile}
                  onUpdateShopProfile={handleUpdateShopProfile}
          userId={user?.uid}
        />
      );
    }

    // お客様向けページのコンテンツ
    switch (appState) {
        case AppState.QUESTIONNAIRE:
          return <Questionnaire onSubmit={handleSubmit} onBack={navigateToWelcome} initialAnswers={answers} error={error} wineList={wineList} occasions={occasions} tastes={tastes} prices={prices} origins={origins} />;
        case AppState.LOADING:
        return <LoadingSpinner type="analyzing" title="分析中" showAdvancedUI={true} />;
        case AppState.RESULT:
        return <Recommendation recommendations={recommendations} wineList={wineList} onReset={navigateToWelcome} />;
        case AppState.WINE_MENU:
        return <WineMenu wineList={wineList} onBack={navigateToWelcome} userId={user?.uid} />;
        case AppState.WELCOME:
        default:
          return (
          <CustomerPage
            shopProfile={shopProfile}
            onStart={handleStart}
            onShowWineMenu={handleShowWineMenu}
            userId={user?.uid}
          />
          );
      }
  }

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)', color: 'var(--theme-text-primary, #374151)' }}>
        {/* 管理者ページの時のみログアウトボタンを表示 */}
        {currentRoute === '/admin' && user && (
          <header className="absolute top-2 left-2 sm:top-4 sm:left-4 md:top-6 md:left-6 z-10">
              {/* ユーザー情報とログアウトボタン */}
              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md">
                <div className="flex items-center space-x-2">
                  {user.photoURL && (
                    <img 
                      src={user.photoURL} 
                      alt="ユーザーアバター" 
                      className="w-6 h-6 rounded-full"
                    />
                  )}
                  <span className="text-sm font-medium" style={{ color: 'var(--theme-text-primary, #374151)' }}>
                    {user.displayName || user.email}
                    {isUserLocked && (
                      <span className="ml-1 text-xs text-green-600" title="ユーザーがロックされています">
                        🔒
                      </span>
                    )}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-xs bg-red-800 text-white px-2 py-1 rounded hover:bg-red-900 transition-colors"
                >
                  ログアウト
                </button>
              </div>
        </header>
        )}
        {/* 管理者ページの時のみバージョンと同期状態を表示 */}
        {currentRoute === '/admin' && (
          <>
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4 md:top-6 md:right-6 z-10 font-semibold text-xs sm:text-sm" style={{ color: 'var(--theme-text-secondary, #6B7280)' }}>
                Ver.2.5.0
        </div>
            {/* データ同期状態の表示 */}
            <div className="absolute top-2 right-20 sm:top-4 sm:right-24 md:top-6 md:right-32 z-10">
          <div className="flex items-center space-x-2">
            {isSyncing ? (
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-blue-600">同期中...</span>
                <button
                  onClick={() => {
                    console.log('手動停止ボタンがクリックされました');
                    setIsSyncing(false);
                    setSyncError('同期を手動で停止しました');
                    // 同期状態をリセットして自動再開を防ぐ
                    setLastSyncTime(null);
                  }}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                  title="同期を停止"
                >
                  停止
                </button>
              </div>
            ) : lastSyncTime && !hasUnsavedChanges ? (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                同期済み
              </span>
            ) : (
              <div className="flex space-x-1">
                <button
                  onClick={async () => {
                    if (!user?.uid) {
                      setSyncError('ログインが必要です');
                      setSyncResult(null);
                      return;
                    }
                    
                    // 手動同期開始のアラート
                    alert(`🔄 手動同期開始\nユーザー: ${user.email}\n現在のワイン数: ${wineList.length}本`);
                    
                    try {
                      setIsSyncing(true);
                      setSyncError(null);
                      setSyncResult(null);
                      
                      console.log('データ同期を開始します...');
                      
                      // 1. まずFirestoreから最新データを取得
                      const loadResult = await getAllSyncedData(user.uid);
                      if (!loadResult.success) {
                        throw new Error(loadResult.error || 'データの取得に失敗しました');
                      }
                      
                      // 2. ローカルデータとFirestoreデータを比較
                      const localData = {
                        wineList,
                        occasions,
                        tastes,
                        prices,
                        origins,
                        shopProfile,
                      };
                      
                      const firestoreData = loadResult.data;
                      
                      // 3. データの変更を検出
                      console.log('ローカルデータのワイン数:', localData.wineList.length);
                      console.log('Firestoreデータのワイン数:', firestoreData.wineList.length);
                      console.log('ローカルワインID一覧:', localData.wineList.map(w => w.id));
                      console.log('FirestoreワインID一覧:', firestoreData.wineList.map(w => w.id));
                      
                      const hasChanges = 
                        JSON.stringify(localData.wineList) !== JSON.stringify(firestoreData.wineList) ||
                        JSON.stringify(localData.occasions) !== JSON.stringify(firestoreData.occasions) ||
                        JSON.stringify(localData.tastes) !== JSON.stringify(firestoreData.tastes) ||
                        JSON.stringify(localData.prices) !== JSON.stringify(firestoreData.prices) ||
                        JSON.stringify(localData.origins) !== JSON.stringify(firestoreData.origins) ||
                        JSON.stringify(localData.shopProfile) !== JSON.stringify(firestoreData.shopProfile);
                      
                      console.log('データに変更があるか:', hasChanges);
                      
                      if (hasChanges) {
                        console.log('ローカルデータに変更を検出しました。Firestoreに保存します。');
                        
                        // 4. ローカルデータをFirestoreに保存
                        console.log('=== 手動同期：Firestore保存開始 ===');
                        console.log('保存するデータ:', localData);
                        const syncResult = await syncAllData(user.uid, localData);
                        console.log('Firestore保存結果:', syncResult);
                        if (!syncResult.success) {
                          console.error('Firestore保存失敗:', syncResult.error);
                          throw new Error(syncResult.error || 'データの保存に失敗しました');
                        }
                        console.log('=== 手動同期：Firestore保存成功 ===');
                        
                        // 5. 保存後のデータでローカル状態を更新
                        setWineList(sortWineList(localData.wineList));
                        setOccasions(localData.occasions);
                        setTastes(localData.tastes);
                        setPrices(localData.prices);
                        setOrigins(localData.origins);
                        setShopProfile(localData.shopProfile);
                        
                        // 6. 同期結果の詳細を表示（ローカルデータの件数）
                        const resultMessage = `✅ 同期完了！\n` +
                          `🍷 ワインリスト: ${localData.wineList.length}本\n` +
                          `🎭 シーン設定: ${localData.occasions.length}個\n` +
                          `👅 味の設定: ${localData.tastes.length}個\n` +
                          `💰 価格設定: ${localData.prices.length}個\n` +
                          `🌍 産地設定: ${localData.origins.length}個\n` +
                          `🏪 店舗名: ${localData.shopProfile.name || '未設定'}\n` +
                          `📤 ローカルの変更をFirestoreに保存しました`;
                        
                        setSyncResult(resultMessage);
                        console.log('ローカルデータをFirestoreに保存しました:', resultMessage);
                      } else {
                        console.log('ローカルデータに変更はありません。Firestoreから最新データを取得します。');
                        
                        // 7. 変更がない場合はFirestoreのデータでローカル状態を更新
                        setWineList(sortWineList(firestoreData.wineList));
                        setOccasions(firestoreData.occasions);
                        setTastes(firestoreData.tastes);
                        setPrices(firestoreData.prices);
                        setOrigins(firestoreData.origins);
                        setShopProfile(firestoreData.shopProfile);
                        
                        // 8. 同期結果の詳細を表示（Firestoreデータの件数）
                        const resultMessage = `✅ 同期完了！\n` +
                          `🍷 ワインリスト: ${firestoreData.wineList.length}本\n` +
                          `🎭 シーン設定: ${firestoreData.occasions.length}個\n` +
                          `👅 味の設定: ${firestoreData.tastes.length}個\n` +
                          `💰 価格設定: ${firestoreData.prices.length}個\n` +
                          `🌍 産地設定: ${firestoreData.origins.length}個\n` +
                          `🏪 店舗名: ${firestoreData.shopProfile.name || '未設定'}\n` +
                          `📥 Firestoreから最新データを取得しました`;
                        
                        setSyncResult(resultMessage);
                        console.log('Firestoreから最新データを取得しました:', resultMessage);
                      }
                      
                      setLastSyncTime(new Date().toLocaleTimeString());
                      setHasUnsavedChanges(false); // 同期完了時に変更フラグをリセット
                      
                      // 手動同期後のDB状態確認
                      try {
                        console.log('=== 手動同期後のDB状態確認開始 ===');
                        const dbCheckResult = await getAllSyncedData(user.uid);
                        if (dbCheckResult.success && dbCheckResult.data) {
                          const dbWineCount = dbCheckResult.data.wineList.length;
                          const dbWineIds = dbCheckResult.data.wineList.map(w => w.id);
                          
                          console.log('=== 手動同期後のDB状態確認結果 ===');
                          console.log('DBのワイン数:', dbWineCount);
                          console.log('DBのワインID一覧:', dbWineIds);
                          
                          // DB状態の結果をアラートで表示
                          alert(`🔍 手動同期後のDB状態確認結果\n` +
                            `DBのワイン数: ${dbWineCount}本\n` +
                            `DBのワインID一覧: ${dbWineIds.join(', ')}\n` +
                            `同期が正常に完了しました`);
                        } else {
                          console.error('手動同期後のDB状態確認失敗:', dbCheckResult.error);
                          alert(`❌ 手動同期後のDB状態確認失敗\nエラー: ${dbCheckResult.error}`);
                        }
                      } catch (dbError) {
                        console.error('手動同期後のDB状態確認エラー:', dbError);
                        alert(`❌ 手動同期後のDB状態確認エラー\nエラー: ${dbError instanceof Error ? dbError.message : '不明なエラー'}`);
                      }
                      
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'データ同期中にエラーが発生しました';
                      setSyncError(errorMessage);
                      setSyncResult(null);
                      console.error('データ同期エラー:', error);
                      console.error('エラーの詳細:', error);
                      console.error('エラーの型:', typeof error);
                      console.error('エラーのスタック:', error instanceof Error ? error.stack : '不明');
                      
                      // エラーメッセージを表示
                      alert(`❌ 手動同期エラー！\nエラー: ${errorMessage}\n\n詳細はコンソールを確認してください`);
                    } finally {
                      setIsSyncing(false);
                    }
                  }}
                  className={`text-xs px-3 py-1 rounded-full font-medium ${
                    hasUnsavedChanges 
                      ? 'text-orange-600 bg-orange-100 hover:bg-orange-200' 
                      : 'text-green-600 bg-green-100 hover:bg-green-200'
                  }`}
                  title={hasUnsavedChanges ? '未保存の変更があります。同期して保存してください。' : '最新データの取得と保存を同時に実行'}
                  disabled={isSyncing}
                >
                  {isSyncing ? '同期中...' : hasUnsavedChanges ? '変更を同期' : 'データ同期'}
                </button>
              </div>
            )}
            {syncError && (
              <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                同期エラー
              </span>
            )}
            {syncResult && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-800 whitespace-pre-line">
                  {syncResult}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {lastSyncTime && `最終同期: ${lastSyncTime}`}
                </div>
              </div>
            )}
          </div>
            </div>
          </>
        )}
        

      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 md:py-16">
        <div className="flex flex-col items-center justify-center min-h-[80vh]">
          {renderContent()}
        </div>
      </main>
      {editingWine && (
        <EditWineModal 
          wine={editingWine} 
          onUpdateWine={handleUpdateWine}
          onClose={handleCancelEdit}
        />
      )}
    </div>
  );
};

export default App;
